/**
 * Read-only / revoked-session helpers.
 *
 * A member (or department admin) of a deactivated department gets a session
 * whose JWT carries `read_only: true`; the backend rejects every non-safe
 * request for it. These helpers let the UI mirror that (defense-in-depth) and
 * handle the forced re-login that follows a department status change.
 */

import { decodeJWT } from "./jwt"

export const READ_ONLY_MESSAGE_KEY = "read_only_message"
export const SESSION_REVOKED_FLAG = "cs_session_revoked"

export const DEFAULT_READ_ONLY_MESSAGE =
  "Your department has been deactivated. You have view-only access until it is reactivated."
export const SESSION_REVOKED_NOTICE = "Your department's status changed. Please sign in again."

/** True when the stored access token carries the `read_only` claim. */
export function isReadOnlySession(): boolean {
  const token = sessionStorage.getItem("access_token")
  if (!token) return false
  return decodeJWT(token)?.read_only === true
}

export function getReadOnlyMessage(): string {
  return sessionStorage.getItem(READ_ONLY_MESSAGE_KEY) || DEFAULT_READ_ONLY_MESSAGE
}

/**
 * The server's explanation is only written at sign-in, while the `read_only`
 * claim is re-issued on every refresh. Drop a stored explanation once the claim
 * is gone so a later read-only period never shows an old one.
 */
export function clearStaleReadOnlyMessage(): void {
  if (!isReadOnlySession()) sessionStorage.removeItem(READ_ONLY_MESSAGE_KEY)
}

/** True for the 401 the backend returns for a session revoked server-side. */
export function isRevokedSessionError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } } | null)?.response
  if (response?.status !== 401) return false
  const detail = response.data?.detail
  return typeof detail === "string" && detail.toLowerCase().includes("revoked")
}

/**
 * Clear auth state and send the user to the login page with an explanation.
 * Deliberately skips the token-refresh path: the refresh token was revoked
 * together with the session, so retrying would only loop.
 */
export function handleRevokedSession(): void {
  sessionStorage.removeItem("access_token")
  sessionStorage.removeItem("refresh_token")
  sessionStorage.removeItem("user")
  sessionStorage.removeItem(READ_ONLY_MESSAGE_KEY)
  sessionStorage.setItem(SESSION_REVOKED_FLAG, SESSION_REVOKED_NOTICE)
  if (!window.location.pathname.includes("/login")) {
    window.location.href = "/login"
  }
}
